import { useEffect, lazy, Suspense } from "react";
import HeroSection from "./components/HeroSection";
import IndianaCommunitiesWeServe from "./components/CommunitiesWeServe";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const CitiesDirectory = lazy(() => import("./components/CitiesDirectory"));
const StateResources = lazy(() => import("./components/StateResources"));
const FAQSection = lazy(() => import("./components/FAQSection"));
const FinalCTA = lazy(() => import("./components/FinalCTA"));

export default function IndianaPage() {
  useEffect(() => {
    document.title =
      "Expedited & Emergency Passport Services Indiana | Indianapolis Passport Agency | Fast Passport Center";

    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "Fast Passport Center provides emergency, expedited, and same-day passport services across all Indiana cities. Serving Indianapolis, Fort Wayne, Evansville, Carmel, and beyond. The Indianapolis Passport Agency is Indiana's only regional passport agency — courier service covers all of Indiana."
      );
    }

    const keywords = document.querySelector('meta[name="keywords"]');
    if (keywords) {
      keywords.setAttribute(
        "content",
        "expedited passport Indiana, emergency passport Indiana, same day passport IN, fast passport Indianapolis, passport renewal Fort Wayne, passport service Evansville, Indianapolis passport agency"
      );
    }

    const geoRegion = document.querySelector('meta[name="geo.region"]');
    if (geoRegion) geoRegion.setAttribute("content", "US-IN");
    const geoPlacename = document.querySelector('meta[name="geo.placename"]');
    if (geoPlacename) geoPlacename.setAttribute("content", "Indiana");
    const geoPos = document.querySelector('meta[name="geo.position"]');
    if (geoPos) geoPos.setAttribute("content", "39.7684;-86.1581");
    const icbm = document.querySelector('meta[name="ICBM"]');
    if (icbm) icbm.setAttribute("content", "39.7684, -86.1581");

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical)
      canonical.setAttribute("href", "https://fastpassportcenter.com/indiana");

    let schema = document.getElementById("in-schema");
    if (!schema) {
      schema = document.createElement("script");
      schema.id = "in-schema";
      schema.type = "application/ld+json";
      document.head.appendChild(schema);
    }

    schema.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Indiana", item: "https://fastpassportcenter.com/indiana" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: "Fast Passport Center - Indiana",
        description:
          "Expedited and emergency passport services across Indiana — Indianapolis, Fort Wayne, Evansville, Carmel, and every city in between.",
        url: "https://fastpassportcenter.com/indiana",
        telephone: "+1-877-253-0084",
        email: "info@fastpassportcenter.com",
        priceRange: "$$",
        areaServed: { "@type": "State", name: "Indiana" },
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "687" },
        openingHoursSpecification: [
          { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "08:00", closes: "20:00" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Does Indiana have its own passport agency?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Indiana has one regional passport agency: the Indianapolis Passport Agency (4419 Indiana Ave, Indianapolis, IN 46221). It is the only regional passport agency in the entire state — Fort Wayne, Evansville, South Bend, and all other Indiana cities must travel to Indianapolis to access it in person.",
            },
          },
          {
            "@type": "Question",
            name: "How fast can I get a passport in Indiana?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "With Fast Passport Center, Indiana residents can get same-day emergency service or 2-3 week expedited processing. Routine service through the post office takes 6-13 weeks plus mailing time.",
            },
          },
          {
            "@type": "Question",
            name: "What makes Indianapolis special for passport service?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Indianapolis is the only city in Indiana with a regional passport agency. This means Indianapolis, Carmel, Fishers, and Noblesville residents have the fastest emergency passport access in Indiana — no long drive required.",
            },
          },
        ],
      },
    ]);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <HeroSection />
      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-gray-50">
            <div className="animate-pulse text-gray-400 text-sm">Loading cities...</div>
          </div>
        }
      >
        <CitiesDirectory />
      </Suspense>
      <IndianaCommunitiesWeServe />
      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-white">
            <div className="animate-pulse text-gray-400 text-sm">Loading resources...</div>
          </div>
        }
      >
        <StateResources />
      </Suspense>

      {/* How We Serve Indiana */}
      <section className="bg-[#F7F8FA] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
              Passport Services
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
              How We Serve Indiana
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
              From the Motor Speedway of Indianapolis to the Ohio River shores of Evansville, from the university halls of Bloomington and Notre Dame to the manufacturing corridors of Fort Wayne — we help Indiana travelers get their passports fast. The Indianapolis Passport Agency serves the entire state.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: "ri-flight-takeoff-line",
                title: "Emergency Same-Day Service",
                desc: "For Indiana travelers with flights within 72 hours. We hand-carry your application to the Indianapolis Passport Agency — no road trip required for most of Indiana.",
              },
              {
                icon: "ri-time-line",
                title: "Expedited 2–9 Day Service",
                desc: "Standard expedited processing with overnight shipping and tracking for Indiana residents. Perfect for Indy 500, spring break, and summer travel.",
              },
              {
                icon: "ri-file-list-3-line",
                title: "New Passport Applications",
                desc: "First-time applicants in Indiana — we handle document prep, photos, and acceptance facility scheduling across all Indiana counties.",
              },
              {
                icon: "ri-refresh-line",
                title: "Passport Renewals",
                desc: "Indiana renewals by mail or courier. We ensure your application is complete — critical before your Indy 500 weekend international trip or Caribbean spring break.",
              },
              {
                icon: "ri-building-line",
                title: "Indianapolis Agency Access",
                desc: "Indiana's only regional passport agency is local to Indianapolis. Fast Passport Center leverages this proximity for the fastest possible service in the state.",
              },
              {
                icon: "ri-error-warning-line",
                title: "Lost, Stolen & Damaged",
                desc: "Fast replacement for Indiana residents who need a new passport quickly — we handle the paperwork and courier delivery to the Indianapolis agency.",
              },
            ].map((svc, i) => (
              <div
                key={i}
                className="bg-white rounded-lg p-7 border border-gray-100 hover:border-[#F6C344]/40 transition-all duration-200"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-5">
                  <i className={`${svc.icon} text-[#1A365D] text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-base mb-2">{svc.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-gray-50">
            <div className="animate-pulse text-gray-400 text-sm">Loading FAQs...</div>
          </div>
        }
      >
        <FAQSection />
      </Suspense>

      <Suspense
        fallback={
          <div className="h-64 flex items-center justify-center bg-white">
            <div className="animate-pulse text-gray-400 text-sm">Loading CTA...</div>
          </div>
        }
      >
        <FinalCTA />
      </Suspense>

      <section className="bg-[#F7F8FA] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <SEOCrossLinks state="IN" showCities={false} />
        </div>
      </section>
    </div>
  );
}